import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarMonth,
  CheckCircle,
  ErrorOutline,
  Info,
  PlayArrow,
  Schedule,
  WarningAmber,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../services/api';
import type { Device, DeviceAvailabilityDay } from '../../types';

interface DeviceAvailabilityDialogProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
}

const formatDateInput = (date: Date) => date.toISOString().slice(0, 10);

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return '0 мин';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  return `${minutes} мин`;
};

const statusMeta = (status: DeviceAvailabilityDay['status']) => {
  switch (status) {
    case 'online':
      return { label: 'Доступен', color: 'success' as const, icon: <CheckCircle fontSize="small" /> };
    case 'partial':
      return { label: 'Частично', color: 'warning' as const, icon: <WarningAmber fontSize="small" /> };
    case 'offline':
      return { label: 'Недоступен', color: 'error' as const, icon: <ErrorOutline fontSize="small" /> };
    default:
      return { label: 'Нет данных', color: 'default' as const, icon: <Info fontSize="small" /> };
  }
};

const SummaryCell: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  progress?: number;
}> = ({ label, value, tone = 'primary', progress }) => (
  <Paper
    elevation={1}
    sx={{
      p: 0.85,
      minWidth: 0,
      borderRadius: 1.25,
      borderLeft: 4,
      borderColor: `${tone}.main`,
    }}
  >
    <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
    <Typography variant="body2" fontWeight={900} noWrap>{value}</Typography>
    {typeof progress === 'number' && (
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, progress))}
        color={tone === 'primary' ? 'primary' : tone}
        sx={{ height: 4, borderRadius: 1, mt: 0.6 }}
      />
    )}
  </Paper>
);

const DeviceAvailabilityDialog: React.FC<DeviceAvailabilityDialogProps> = ({ open, onClose, device }) => {
  const now = useMemo(() => new Date(), []);
  const initialFrom = useMemo(() => {
    const date = new Date(now);
    date.setDate(date.getDate() - 29);
    return formatDateInput(date);
  }, [now]);
  const initialTo = useMemo(() => formatDateInput(now), [now]);

  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['device-availability-calendar', device?.id, dateFrom, dateTo],
    queryFn: () => api.getDeviceAvailabilityCalendar(device!.id, {
      date_from: `${dateFrom}T00:00:00+03:00`,
      date_to: `${dateTo}T23:59:59+03:00`,
      timezone_name: 'Europe/Moscow',
    }),
    enabled: open && Boolean(device?.id),
  });

  const days = query.data?.days || [];
  const selectedDay = useMemo(() => {
    if (!days.length) return null;
    return days.find((day) => day.date === selectedDate) || days.find((day) => day.status === 'partial') || days[days.length - 1];
  }, [days, selectedDate]);

  const calendarOffset = useMemo(() => {
    if (!days.length) return 0;
    const first = new Date(`${days[0].date}T00:00:00`);
    const jsDay = first.getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
  }, [days]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ py: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <CalendarMonth color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={900}>Календарь доступности</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {device?.name || 'Устройство'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5 }}>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              label="С"
              type="date"
              size="small"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="По"
              type="date"
              size="small"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button size="small" variant="outlined" onClick={() => query.refetch()} disabled={query.isFetching}>
              Обновить
            </Button>
            {query.isFetching && <CircularProgress size={20} />}
          </Stack>

          {query.isError && (
            <Alert severity="error">
              Не удалось загрузить календарь доступности: {(query.error as Error)?.message || 'ошибка запроса'}
            </Alert>
          )}

          {query.data && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(6, minmax(0, 1fr))',
                  },
                  gap: 0.8,
                }}
              >
                <SummaryCell label="Доступность" value={`${query.data.summary.uptime_percentage}%`} progress={query.data.summary.uptime_percentage} />
                <SummaryCell label="Онлайн" value={formatDuration(query.data.summary.online_seconds)} tone="success" />
                <SummaryCell label="Недоступен" value={formatDuration(query.data.summary.offline_seconds)} tone="error" />
                <SummaryCell label="Воспроизведение" value={formatDuration(query.data.summary.playback_seconds)} tone="info" />
                <SummaryCell label="Proof-of-play" value={query.data.summary.proof_count} tone="primary" />
                <SummaryCell label="Частичных дней" value={query.data.summary.days_partial} tone="warning" />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                  gap: 0.55,
                }}
              >
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((label) => (
                  <Typography key={label} variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {label}
                  </Typography>
                ))}
                {Array.from({ length: calendarOffset }).map((_, index) => (
                  <Box key={`empty-${index}`} />
                ))}
                {days.map((day) => {
                  const meta = statusMeta(day.status);
                  const isSelected = selectedDay?.date === day.date;
                  const date = new Date(`${day.date}T00:00:00`);
                  return (
                    <Tooltip
                      key={day.date}
                      title={`${meta.label}: ${day.uptime_percentage}% • playback ${formatDuration(day.playback_seconds)} • событий ${day.events.length}`}
                      arrow
                    >
                      <Button
                        variant={isSelected ? 'contained' : 'outlined'}
                        color={meta.color}
                        onClick={() => setSelectedDate(day.date)}
                        sx={{
                          minWidth: 0,
                          height: 54,
                          p: 0.45,
                          alignItems: 'stretch',
                          justifyContent: 'space-between',
                          flexDirection: 'column',
                          textTransform: 'none',
                        }}
                      >
                        <Typography variant="caption" sx={{ lineHeight: 1 }}>
                          {date.getDate()}
                        </Typography>
                        <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 900 }}>
                          {day.uptime_percentage}%
                        </Typography>
                        <Typography variant="caption" sx={{ lineHeight: 1 }}>
                          {day.proof_count ? `${day.proof_count} play` : meta.label}
                        </Typography>
                      </Button>
                    </Tooltip>
                  );
                })}
              </Box>

              {selectedDay && (
                <Box>
                  <Divider sx={{ mb: 1.25 }} />
                  <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
                    <Chip icon={statusMeta(selectedDay.status).icon} label={`${selectedDay.date}: ${statusMeta(selectedDay.status).label}`} color={statusMeta(selectedDay.status).color} />
                    <Chip icon={<Schedule fontSize="small" />} label={`Онлайн ${formatDuration(selectedDay.online_seconds)}`} variant="outlined" />
                    <Chip label={`Офлайн ${formatDuration(selectedDay.offline_seconds)}`} variant="outlined" />
                    <Chip icon={<PlayArrow fontSize="small" />} label={`Проиграно ${formatDuration(selectedDay.playback_seconds)}`} variant="outlined" />
                    <Chip label={`Медиа: ${selectedDay.unique_media_count}`} variant="outlined" />
                    <Chip label={`Плейлисты: ${selectedDay.unique_playlist_count}`} variant="outlined" />
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.25fr) minmax(0, 1fr)' },
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Воспроизведения</Typography>
                      <TableContainer sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Начало</TableCell>
                              <TableCell>Контент</TableCell>
                              <TableCell>Плейлист</TableCell>
                              <TableCell>Длительность</TableCell>
                              <TableCell>Статус</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedDay.playbacks.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography variant="body2" color="text.secondary">Нет proof-of-play записей за день.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            {selectedDay.playbacks.map((playback) => (
                              <TableRow key={playback.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(playback.started_at)}</TableCell>
                                <TableCell><Typography variant="caption" noWrap sx={{ display: 'block', maxWidth: 240 }}>{playback.media_title || (playback.media_id ? `Media #${playback.media_id}` : '—')}</Typography></TableCell>
                                <TableCell><Typography variant="caption" noWrap sx={{ display: 'block', maxWidth: 220 }}>{playback.playlist_name || (playback.playlist_id ? `Playlist #${playback.playlist_id}` : '—')}</Typography></TableCell>
                                <TableCell>{formatDuration(playback.duration_actual)}</TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={0.5}>
                                    {playback.completed && <Chip size="small" label="completed" color="success" variant="outlined" />}
                                    {playback.interrupted && <Chip size="small" label="interrupted" color="warning" variant="outlined" />}
                                    {!playback.completed && !playback.interrupted && <Chip size="small" label="open" variant="outlined" />}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>События доступности</Typography>
                      <TableContainer sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Время</TableCell>
                              <TableCell>Тип</TableCell>
                              <TableCell>Сообщение</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedDay.events.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={3}>
                                  <Typography variant="body2" color="text.secondary">Событий доступности за день нет.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            {selectedDay.events.map((event) => (
                              <TableRow key={event.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(event.timestamp)}</TableCell>
                                <TableCell><Typography variant="caption" noWrap>{event.event_type}</Typography></TableCell>
                                <TableCell><Typography variant="caption" noWrap sx={{ display: 'block', maxWidth: 360 }}>{event.message}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceAvailabilityDialog;
